import type { EstimateDetail, LineItem, Tuple } from "@/interfaces/estimate-detail";
import type { LineItemData } from "@/interfaces/payloads/line-item";
import { getDetailFromPossiblyMergedPage } from "./lookup";

export interface BatchOperation {
  pageId: string;
  tupleId: string;
  lineItemId: string;
  action: "confirm" | "remove";
}

export interface BatchApiOperation {
  lineItemId: string;
  estimateId: string;
  pageId: string;
  lineItemData: LineItemData;
  source?: string;
  metadata?: Record<string, unknown>;
}

export interface BatchOperationsResult {
  modifiedState: EstimateDetail[];
  batchApiPayload: BatchApiOperation[];
}

/**
 *
 * Processes batch confirm/remove operations on tuples and returns the modified state
 * along with the API payload needed to persist the changes.
 *
 * For "confirm": sets `hasError: false`, `hadErrorThatHasBeenFixed: true`, marks the
 * target line item as confirmed with full confidence, and processes any recommendation
 * tuples (complements) linked to the confirmed tuple.
 *
 * For "remove": marks the tuple and all its line items as deleted.
 *
 * @param state - The current estimation detail state
 * @param operations - The batch operations to apply
 * @param estimateId - The estimate ID for the API payload
 * @returns The modified state and batch API payload, or null if state is empty
 */
export function prepareBatchOperations(
  state: EstimateDetail[],
  operations: BatchOperation[],
  estimateId: string
): BatchOperationsResult | null {
  if (!state || operations.length === 0) return null;

  // Each iteration threads `workingState` forward via immutable updates so untouched rooms keep
  // their references and downstream memoization isn't defeated.
  let workingState = state;
  const batchApiPayload: BatchApiOperation[] = [];

  for (const operation of operations) {
    const { pageId: initialPageId, tupleId, lineItemId, action } = operation;

    const { currentPageIndex, tuples, tupleIndex } = getDetailFromPossiblyMergedPage(workingState, initialPageId, tupleId);
    const room = workingState[currentPageIndex];
    const currentTuple = tuples[tupleIndex];

    if (!room || !currentTuple) continue;

    const lineItemIndex = currentTuple.lineItems.findIndex((item) => item.lineItemId === lineItemId);

    // A confirm targets a specific line item by id. If it isn't found, the confirm branch below
    // would mark the tuple as resolved while confirming nothing (`index === -1` is never true) and
    // still push an incorrect API payload — so skip it. Remove operates on the whole tuple by
    // tupleId and never reads lineItemIndex, so it's unaffected.
    if (action === "confirm" && lineItemIndex === -1) continue;

    const pageId = room.dataForUI.metadata.pageId;
    const lineItem = currentTuple.lineItems[lineItemIndex];
    const orderIndex = lineItem?.orderIndex;

    let updatedTuple: Tuple;
    if (action === "remove") {
      updatedTuple = {
        ...currentTuple,
        deleted: true,
        lineItems: currentTuple.lineItems.map((li) => ({ ...li, deleted: true })),
      };
    } else {
      updatedTuple = {
        ...currentTuple,
        hasError: false,
        hadErrorThatHasBeenFixed: true,
        ...(currentTuple.isFreeFormFlag && { isFreeFormFlag: false }),
        ...(lineItemIndex === 0 && orderIndex !== undefined && { originalTupleIndex: orderIndex }),
        lineItems: currentTuple.lineItems.map((item, index) => ({
          ...item,
          confirmed: index === lineItemIndex,
          confidence: index === lineItemIndex ? 1 : item.confidence,
        })),
      };
    }

    let newTuples = tuples.map((t, i) => (i === tupleIndex ? updatedTuple : t));

    batchApiPayload.push({
      lineItemId: tupleId,
      estimateId,
      pageId,
      lineItemData: [updatedTuple] as unknown as LineItemData,
      source: action === "remove" ? "" : "ErrorResolutionPanel",
      metadata: {},
    });

    // Process recommendation tuples (complements) for confirm action
    if (action === "confirm" && lineItemIndex === 0 && currentTuple.lineItems.length > 1) {
      const recommendationTuples = newTuples.filter((t: Tuple) => t.isComplementOf === currentTuple.tupleId);

      for (const recommendationTuple of recommendationTuples) {
        const updatedRecommendationTuple: Tuple = {
          ...recommendationTuple,
          hasError: false,
          isLikely: true,
          hadErrorThatHasBeenFixed: true,
          lineItems: recommendationTuple.lineItems.map((item: LineItem) => ({
            ...item,
            isComplement: true,
          })),
        };
        const recIdx = newTuples.findIndex((t) => t.tupleId === recommendationTuple.tupleId);
        newTuples = newTuples.map((t, i) => (i === recIdx ? updatedRecommendationTuple : t));

        batchApiPayload.push({
          lineItemId: recommendationTuple.tupleId,
          estimateId,
          pageId,
          lineItemData: [updatedRecommendationTuple] as unknown as LineItemData,
          source: "ErrorResolutionPanel",
          metadata: {},
        });
      }
    }

    const newRoom: EstimateDetail = {
      ...room,
      dataForUI: {
        ...room.dataForUI,
        page: {
          ...room.dataForUI.page,
          tuplesOutput: newTuples,
        },
      },
    };
    workingState = workingState.map((r, i) => (i === currentPageIndex ? newRoom : r));
  }

  return { modifiedState: workingState, batchApiPayload };
}
